import ArrowDropDownIcon from '@material-design-icons/svg/filled/arrow_drop_down.svg?react';
import KeyboardArrowDownIcon from '@material-design-icons/svg/filled/keyboard_arrow_down.svg?react';
import LogoutIcon from '@material-design-icons/svg/filled/logout.svg?react';
import SettingsIcon from '@material-design-icons/svg/filled/settings.svg?react';
import { useEffect, useRef, useState } from 'react';
import type { RightResult, RouterOutputs } from '../../types/TRPC';
import { LogoFull } from '../LogoFull';
import { S3Image } from '../S3Image';

declare global {
  interface Window {
    OneTrust?: {
      ToggleInfoDisplay?: () => void;
    };
  }
}

type PublisherSummary = RightResult<RouterOutputs['getActivePublisher']>;

export interface SidebarHeaderProps {
  organizationName: string;
  publisherAvatarUrl: string | null;
  isDropdownOpen: boolean;
  activePublisherUserJoinId?: string | null;
  accessiblePublishers: PublisherSummary[];
  isSwitchingPublisher?: boolean;
  disablePublisherSwitching?: boolean;
  onDropdownToggle: () => void;
  onSwitchPublisher: (publisherUserJoinId: string) => void;
  onSettings: () => void;
  onSignOut: () => void;
}

export const SidebarHeader = ({
  organizationName,
  publisherAvatarUrl,
  isDropdownOpen,
  activePublisherUserJoinId,
  accessiblePublishers,
  isSwitchingPublisher = false,
  disablePublisherSwitching = false,
  onDropdownToggle,
  onSwitchPublisher,
  onSettings,
  onSignOut,
}: SidebarHeaderProps) => {
  const dropdownRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const [isLegalOpen, setIsLegalOpen] = useState(false);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target as Node)
      ) {
        // This would need to be handled by parent component
        // For now, we'll rely on the parent's effect
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isDropdownOpen]);

  return (
    <div className="w-full flex flex-col justify-start items-center relative">
      <div className="w-64 pt-4 px-4 bg-black/60 inline-flex justify-start items-center gap-2">
        <div className="flex-1 flex justify-start items-center gap-2">
          <div className="size-6 bg-zinc-800 rounded-lg shadow-[inset_0px_0.375px_0.375px_0px_rgba(255,255,255,0.17)] overflow-hidden">
            {publisherAvatarUrl ? (
              <S3Image
                src={publisherAvatarUrl}
                alt=""
                aria-hidden="true"
                className="w-full h-full object-cover"
                useThumbnail={false}
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-zinc-600 text-xs font-semibold">
                {organizationName.charAt(0).toUpperCase()}
              </div>
            )}
          </div>
          <div
            ref={triggerRef}
            className="flex-1 flex justify-start items-center gap-1.5 cursor-pointer hover:opacity-80 transition-all duration-300 ease-in-out group"
            onClick={onDropdownToggle}
          >
            <div className="text-white text-sm font-bold leading-tight">
              {organizationName}
            </div>
            <KeyboardArrowDownIcon
              className={`transition-all duration-300 ease-in-out ${isDropdownOpen ? 'rotate-180' : ''}`}
              style={{ fill: 'white' }}
            />
          </div>
        </div>
      </div>

      {isDropdownOpen && (
        <div
          ref={dropdownRef}
          className="absolute top-14 left-4 right-4 bg-neutral-900 rounded-lg border border-neutral-800 inline-flex flex-col justify-start items-start overflow-hidden z-50"
        >
          {accessiblePublishers.length > 0 && (
            <>
              <div className="w-full border-b border-neutral-800 px-3 py-2">
                <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-neutral-500">
                  Publishers
                </div>
              </div>
              <div className="w-full py-1">
                {accessiblePublishers.map((publisher) => {
                  const isActive =
                    publisher.publisherUserJoinId === activePublisherUserJoinId;

                  return (
                    <button
                      key={publisher.publisherUserJoinId}
                      type="button"
                      disabled={
                        disablePublisherSwitching || isSwitchingPublisher
                      }
                      onClick={() =>
                        onSwitchPublisher(publisher.publisherUserJoinId)
                      }
                      className={`flex w-full items-center gap-3 px-3 py-2 text-left transition-colors duration-200 ${
                        isActive ? 'bg-[#34FFC2]/10' : 'hover:bg-neutral-800/60'
                      } ${
                        disablePublisherSwitching || isSwitchingPublisher
                          ? 'cursor-not-allowed opacity-60'
                          : ''
                      }`}
                    >
                      <div className="flex size-8 items-center justify-center overflow-hidden rounded-lg bg-zinc-800 text-xs font-semibold text-zinc-200">
                        {publisher.avatarThumbnailUrl || publisher.avatarUrl ? (
                          <S3Image
                            src={
                              publisher.avatarThumbnailUrl ||
                              publisher.avatarUrl ||
                              ''
                            }
                            alt=""
                            aria-hidden="true"
                            className="h-full w-full object-cover"
                            useThumbnail={false}
                          />
                        ) : (
                          publisher.name.charAt(0).toUpperCase()
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-medium text-white">
                          {publisher.name}
                        </div>
                        <div className="flex items-center gap-2 text-xs text-neutral-400">
                          <span>{publisher.role}</span>
                          <span>
                            {publisher.publisherType === 'Individual'
                              ? 'Individual'
                              : 'Organization'}
                          </span>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
              <div className="w-full border-t border-neutral-800" />
            </>
          )}
          <div className="self-stretch inline-flex justify-start items-start">
            <button
              type="button"
              aria-label="Settings"
              onClick={onSettings}
              className="flex-1 h-9 pl-2 pr-8 py-3.5 rounded flex justify-center items-center gap-2 hover:bg-neutral-800 transition-colors duration-200"
            >
              <div className="flex justify-start items-center gap-1">
                <div className="size-6 relative rounded-[20px]">
                  <div className="size-4 left-[4px] top-[4px] absolute overflow-hidden">
                    <SettingsIcon className="size-3.5 left-[1.21px] top-[1.02px] absolute fill-white" />
                  </div>
                </div>
              </div>
              <div className="flex-1 flex justify-start items-center gap-36">
                <div className="flex-1 flex justify-start items-center gap-28">
                  <div className="text-center justify-start text-neutral-400 text-sm font-normal font-['Inter'] leading-tight">
                    Settings
                  </div>
                </div>
              </div>
            </button>
          </div>
          <div className="self-stretch inline-flex justify-start items-start">
            <button
              type="button"
              aria-label="Sign Out"
              onClick={onSignOut}
              className="flex-1 h-9 pl-2 pr-8 py-3.5 rounded flex justify-center items-center gap-2 hover:bg-neutral-800 transition-colors duration-200"
            >
              <div className="flex justify-start items-center gap-1">
                <div className="size-6 relative rounded-[20px]">
                  <div className="size-4 left-[4px] top-[4px] absolute overflow-hidden">
                    <LogoutIcon className="w-3 h-3.5 left-[2px] top-[1px] absolute fill-white" />
                  </div>
                </div>
              </div>
              <div className="flex-1 flex justify-start items-center gap-36">
                <div className="flex-1 flex justify-start items-center gap-28">
                  <div className="text-center justify-start text-neutral-400 text-sm font-normal font-['Inter'] leading-tight">
                    Sign Out
                  </div>
                </div>
              </div>
            </button>
          </div>
          <div className="w-full border-t border-neutral-800" />
          <div className="w-full flex flex-col items-start gap-4 px-4 py-4">
            <LogoFull type="light" className="h-3" />
            <div className="w-full flex flex-col">
              <button
                type="button"
                aria-expanded={isLegalOpen}
                onClick={() => setIsLegalOpen((open) => !open)}
                className="w-full flex justify-between items-center text-neutral-500 text-sm font-normal font-['Inter'] leading-tight"
              >
                <span className="text-xs">Legal &amp; Privacy</span>
                <ArrowDropDownIcon
                  className={`transition-transform duration-200 ${isLegalOpen ? 'rotate-180' : ''}`}
                  style={{ fill: 'currentColor' }}
                />
              </button>
              {isLegalOpen && (
                <div className="flex flex-col gap-2.5 text-xs text-neutral-400 whitespace-nowrap mt-3">
                  <a
                    href="https://docs.solanamobile.com/dapp-store/agreement"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-white transition-colors duration-200"
                  >
                    Terms of Service
                  </a>
                  <a
                    href="https://docs.solanamobile.com/dapp-store/privacy-policy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-white transition-colors duration-200"
                  >
                    Privacy Policy
                  </a>
                  <a
                    href="https://solanamobile.com/cookie-policy-web"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-white transition-colors duration-200"
                  >
                    Cookie Policy
                  </a>
                  <a
                    href="https://docs.solanamobile.com/dapp-store/privacy-policy"
                    data-privacy-choices
                    onClick={(event) => {
                      if (
                        typeof window.OneTrust?.ToggleInfoDisplay === 'function'
                      ) {
                        event.preventDefault();
                        window.OneTrust.ToggleInfoDisplay();
                      }
                    }}
                    className="hover:text-white transition-colors duration-200"
                  >
                    Your Privacy Choices
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
